import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useFormikContext, Form } from 'formik'
import { Box, Button, useTheme, Grid, useMediaQuery, Typography } from '@mui/material'
import { jwtDecode } from 'jwt-decode'

import { E2FAMethod, ETranslationNs, IVerificationCodeForm } from 'src/types'

import { useStyles } from '../styles'
import { TrackgoodLogo } from 'src/assets/icons'
import { FONTS, SPACES } from 'src/constants'
import { useSendLogin2FAVerificationEmail } from 'src/hooks'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { useGetLogin2FAVerificationEmailTime } from 'src/hooks/auth/use-get-2fa-verification-emai-time.hook'
import useTimer from 'src/hooks/common/use-timer'
import { useGetToken } from 'src/hooks/auth/use-get-token'
import { handleTrimValueOnPaste } from 'src/common/handlers'
import { TextInputWithHelper } from 'src/components/text-input-with-helper-icon'

const SignIn2FAVerificationForm = () => {
  const [t] = useTranslation()
  const [searchParams] = useSearchParams()
  const { errors, values, handleChange, isSubmitting, setFieldValue } =
    useFormikContext<IVerificationCodeForm>()
  const { mutate: sendCode, isLoading: isSending } = useSendLogin2FAVerificationEmail()

  const theme = useTheme()
  const isLaptop = useMediaQuery(theme.breakpoints.down('laptop'))
  const classes = useStyles()

  const method = searchParams.get('method')
  const token = searchParams.get('token')
  const { data: tokenData, isLoading: isTokenLoading } = useGetToken(token)
  const [isFirstResend, setIsFirstResend] = useState(tokenData?.payload?.isFirstResend)

  let userId = null
  if (token) {
    const decodedToken: { userId: string } = jwtDecode(token)
    userId = decodedToken.userId
  }

  const { data, isLoading } = useGetLogin2FAVerificationEmailTime(userId)
  const startTime = data && data.status ? new Date(data.time).getTime() : 0
  const { timeLeft, formatTimeLeft } = useTimer(startTime)

  useEffect(() => {
    if (!isTokenLoading) {
      setIsFirstResend(tokenData?.payload?.isFirstResend)
    }
  }, [isTokenLoading, tokenData])

  useEffect(() => {
    if (!token || method !== E2FAMethod.Email) return

    if (isFirstResend && !isTokenLoading) {
      sendCode({ token })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isTokenLoading, tokenData, isFirstResend])

  const handleSend2FAEmailCode = () => {
    if (!token) return
    sendCode({ token, enableTimeout: true })
  }

  return (
    <Form>
      <Grid container direction="column" justifyContent="center" alignItems="center">
        <TrackgoodLogo className={classes.labelMobile} />
        <Grid item xs={12} className={classes.containerTitle}>
          <Typography
            mb={isLaptop ? SPACES.xxxs : 0}
            fontWeight={'700'}
            fontSize={FONTS.SIZES.xxxxl}
          >
            {t('sign-in-2fa-verification-title.v')}
          </Typography>
        </Grid>
        <Grid item xs={12} mb={2} className={classes.containerField}>
          <Box sx={{ width: '100%', mb: '12px' }}>
            <TextInputWithHelper
              fullWidth
              withoutPlaceholderPrepend
              inputName="code"
              handleChange={handleChange}
              value={values.code}
              error={errors.code ? t(errors.code + '.v') : ''}
              inputPlaceholder={t('signup-email-verify-code-input-placeholder.v')}
              inputUpperLabel={
                <HelperWrapper
                  t={'signup-email-verify-code-input-label'}
                  ns={ETranslationNs.Common}
                  isInputLable
                  isMandatory
                />
              }
              handlePaste={(event) =>
                handleTrimValueOnPaste(event, values.code, setFieldValue, 'code')
              }
            />
          </Box>
          {method === E2FAMethod.Email && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Button
                onClick={handleSend2FAEmailCode}
                disabled={
                  isSending || (timeLeft > 0 && !isFirstResend) || isLoading || isTokenLoading
                }
                type="button"
                size="small"
              >
                {t('sign-in-2fa-verification-email-send.v')}
              </Button>
              {timeLeft > 0 && !isTokenLoading && !isFirstResend && (
                <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.26)' }}>
                  {formatTimeLeft(timeLeft)}
                </Typography>
              )}
            </Box>
          )}
        </Grid>
        <Grid item xs={6} display="flex" flexDirection={'column'} width={'100%'}>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || Object.values(errors).length > 0}
            color="secondary"
            sx={{ width: '100%', color: '#fff', height: '46px', mb: 2 }}
          >
            {t('signup-email-verify-submit-button.v')}
          </Button>
        </Grid>
      </Grid>
    </Form>
  )
}

export { SignIn2FAVerificationForm }
